import { useEffect, useState } from "react";
import { Loader2, PenLine } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { SPECIALTY_CODES, SPECIALTY_LABELS, type SpecialtyCode } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { AJUDA_EQUIPE_E_PARCEIROS } from "@/lib/ajuda/dialogos/equipe-e-parceiros";
import { usePermissao } from "@/lib/use-permissao";
import { Avatar } from "@/components/Avatar";
import { ResponsabilidadesDoMembro } from "./ResponsabilidadesDoMembro";
import { resolveDashboardMode } from "@/lib/dashboard-mode";
import { getRoleLabel } from "./role-meta";
import { AcessoDoMembro } from "./AcessoDoMembro";
import { TabSaveBar } from "@/components/profile/TabSaveBar";
import { SeletorDeEspecialidades } from "@/components/services/SeletorDeEspecialidades";

function Secao({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
        {title}
      </h3>
      {children}
    </section>
  );
}

type Especialidade = {
  codigos: string[];
  rotulos: string[];
  origem: "organizacao" | "perfil" | "nenhuma";
};

/**
 * Especialidade da pessoa NESTA organização.
 *
 * Dois níveis convivem: a pessoal, que ela escolhe no perfil e leva pra onde trabalhar, e a da
 * clínica, que é o que ESTA organização diz sobre ela. Enquanto a clínica não opina, a leitura herda
 * o perfil — e a tela diz de onde veio, senão o admin não distingue "já foi definido aqui" de "está
 * pegando emprestado".
 *
 * Desmarcar tudo não é "sem especialidade": é devolver a leitura ao perfil pessoal.
 */
function EspecialidadeDoVinculo({
  membershipId,
  especialidade,
  podeEditar,
}: {
  membershipId: string;
  especialidade?: Especialidade;
  podeEditar: boolean;
}) {
  const utils = trpc.useUtils();
  const [editando, setEditando] = useState(false);
  const [escolhidas, setEscolhidas] = useState<string[]>([]);
  const ativasDaClinica = trpc.organization.activeSpecialties.useQuery(undefined, { enabled: editando });

  const salvar = trpc.team.updateSpecialties.useMutation({
    onSuccess: () => {
      toast.success("Especialidade atualizada");
      utils.team.getById.invalidate({ membershipId });
      utils.team.list.invalidate();
      setEditando(false);
    },
    onError: (e) => toast.error(e.message || "Falha ao salvar especialidade"),
  });

  const atuais = especialidade?.codigos ?? [];
  const origem = especialidade?.origem ?? "nenhuma";

  if (!editando) {
    return (
      <div className="space-y-1.5">
        {atuais.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {atuais.map((code) => (
              <span
                key={code}
                className="rounded-full bg-brand-50 px-2.5 py-1 text-[11px] font-medium text-brand-700 dark:bg-primary/10 dark:text-primary"
              >
                {SPECIALTY_LABELS[code as SpecialtyCode] ?? code}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-[12.5px] text-zinc-400 dark:text-muted-foreground/70">Não informada</p>
        )}
        <p className="text-[11px] text-zinc-400 dark:text-muted-foreground/70">
          {origem === "organizacao"
            ? "Definida por esta organização."
            : origem === "perfil"
              ? "Herdada do perfil pessoal — esta organização ainda não definiu a dela."
              : "Nem o perfil pessoal nem esta organização informaram."}
        </p>
        {podeEditar && (
          <button
            type="button"
            onClick={() => {
              setEscolhidas(origem === "organizacao" ? atuais : []);
              setEditando(true);
            }}
            className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 px-2.5 py-1 text-[11px] font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted"
          >
            <PenLine className="h-3 w-3" />
            {origem === "organizacao" ? "Alterar" : "Definir para esta organização"}
          </button>
        )}
      </div>
    );
  }

  // As da clínica à vista; as outras atrás de "Mostrar outras" (mesmo seletor do procedimento).
  const daClinica = (ativasDaClinica.data ?? []).map((e) => e.code).filter((c) => c in SPECIALTY_LABELS);
  const opcao = (code: string) => ({ id: code, label: SPECIALTY_LABELS[code as SpecialtyCode] ?? code });

  return (
    <div className="space-y-2">
      <SeletorDeEspecialidades
        principais={daClinica.map(opcao)}
        outras={SPECIALTY_CODES.filter((c) => !daClinica.includes(c)).map(opcao)}
        marcadas={escolhidas}
        onChange={setEscolhidas}
      />
      <p className="text-[11px] text-zinc-400 dark:text-muted-foreground/70">
        {escolhidas.length === 0
          ? "Sem nenhuma marcada, volta a herdar o perfil pessoal desta pessoa."
          : "Vale só nesta organização — o perfil pessoal não muda."}
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={salvar.isPending}
          onClick={() => salvar.mutate({ membershipId, specialtyCodes: escolhidas })}
          className="inline-flex h-8 items-center gap-1.5 rounded-md bg-brand-600 px-3 text-[12px] font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-60"
        >
          {salvar.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
          Salvar
        </button>
        <button
          type="button"
          onClick={() => setEditando(false)}
          className="inline-flex h-8 items-center rounded-md border border-zinc-200 px-3 text-[12px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}

type Props = {
  membershipId: string | null;
  onClose: () => void;
};

/** Perfil de um membro da equipe: acesso, especialidade e as responsabilidades — os procedimentos
 *  que ele atende (ordenam e sugerem na agenda e no plano, sem restringir nada). */
export function TeamMemberDrawer({ membershipId, onClose }: Props) {
  const open = !!membershipId;
  const utils = trpc.useUtils();
  const [selectedIds, setSelectedIds] = useState<string[] | null>(null);

  const detail = trpc.team.getById.useQuery(
    { membershipId: membershipId ?? "" },
    { enabled: open },
  );
  const meQuery = trpc.me.current.useQuery();
  const mode = resolveDashboardMode(meQuery.data?.activeOrganization?.types ?? []);
  const podeGerenciarEquipe = usePermissao("team.manage");

  useEffect(() => {
    setSelectedIds(null);
  }, [membershipId]);

  const update = trpc.team.updateAssignedProcedures.useMutation({
    onSuccess: () => {
      toast.success("Responsabilidades atualizadas");
      utils.team.getById.invalidate({ membershipId: membershipId ?? "" });
      utils.team.list.invalidate();
      utils.organization.listDentists.invalidate();
    },
    onError: (err) => toast.error(err.message || "Falha ao salvar"),
  });

  const data = detail.data as any;
  // Nunca customizado (assignedProcedureTypeIds vazio) -> parte da sugestão calculada por
  // especialidade; depois disso, o que está selecionado na tela é sempre a fonte da verdade.
  const baseline: string[] =
    selectedIds ??
    (data?.assignedProcedureTypeIds?.length ? data.assignedProcedureTypeIds : data?.suggestedProcedureTypeIds ?? []);

  const dirty =
    !!data && JSON.stringify([...baseline].sort()) !== JSON.stringify([...(data.assignedProcedureTypeIds ?? [])].sort());

  const toggle = (service: { id: string }) => {
    const current = selectedIds ?? baseline;
    setSelectedIds(
      current.includes(service.id)
        ? current.filter((id) => id !== service.id)
        : [...current, service.id],
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        closeLabel="Fechar perfil do colaborador"
        className="flex max-h-[85dvh] max-w-2xl flex-col gap-0 overflow-hidden border-zinc-200 bg-white p-0 shadow-xl dark:border-border dark:bg-card"
      >
        {/* `pr-12` reserva a faixa do "X" (44px) pra que nome comprido não passe por baixo dele. */}
        <DialogHeader className="flex-row items-center gap-2.5 space-y-0 border-b border-zinc-100 px-5 py-4 pr-12 text-left dark:border-border">
          <Avatar src={data?.user?.image} name={data?.user?.name} size="sm" />
          <div className="min-w-0">
            <DialogTitle
              className="truncate text-sm font-semibold text-zinc-900 dark:text-foreground"
              ajuda={AJUDA_EQUIPE_E_PARCEIROS.membroDaEquipe}
            >
              {data?.user?.name || "(sem nome)"}
            </DialogTitle>
            <DialogDescription className="truncate text-xs text-zinc-500 dark:text-muted-foreground">
              {data?.customRole?.label ?? getRoleLabel(data?.role, mode)}
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4">
          {detail.isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
            </div>
          ) : (
            <>
              <Secao title="Acesso">
                <div className="space-y-2.5 rounded-lg border border-zinc-100 p-3 dark:border-border">
                  <div>
                    <p className="mb-1 text-[10px] font-medium uppercase tracking-wider text-zinc-400 dark:text-muted-foreground/70">
                      E-mail
                    </p>
                    <p className="text-sm text-zinc-700 dark:text-foreground">{data?.user?.email}</p>
                  </div>
                  {!podeGerenciarEquipe && (
                    <div>
                      <p className="mb-1 text-[10px] font-medium uppercase tracking-wider text-zinc-400 dark:text-muted-foreground/70">
                        Tipo de acesso
                      </p>
                      <p className="text-sm text-zinc-700 dark:text-foreground">
                        {data?.customRole?.label ?? getRoleLabel(data?.role, mode)}
                      </p>
                    </div>
                  )}
                </div>
              </Secao>

              {podeGerenciarEquipe && data && (
                <Secao title="Tipo de acesso e permissões">
                  <AcessoDoMembro
                    membro={{ id: membershipId!, role: data.role, customRole: data.customRole, user: data.user }}
                    mode={mode}
                    tipos={meQuery.data?.activeOrganization?.types ?? []}
                    aberto={open}
                  />
                </Secao>
              )}

              <Secao title="Especialidade">
                <EspecialidadeDoVinculo
                  membershipId={membershipId!}
                  especialidade={data?.especialidade}
                  podeEditar={podeGerenciarEquipe}
                />
              </Secao>

              <Secao title="Responsabilidades">
                <p className="mb-2.5 text-[11.5px] leading-relaxed text-zinc-500 dark:text-muted-foreground">
                  Usado pra organizar e sugerir profissionais. Não restringe acesso nem impede
                  outros atendimentos.
                </p>
                {!data?.assignedProcedureTypeIds?.length && data?.suggestedProcedureTypeIds?.length > 0 && (
                  <p className="mb-2 text-[11.5px] text-zinc-500">
                    Sugestão pré-marcada com base na especialidade — ajuste como quiser.
                  </p>
                )}
                <ResponsabilidadesDoMembro
                  procedimentos={data?.services ?? []}
                  marcados={baseline}
                  especialidadesDoMembro={data?.especialidade?.codigos ?? []}
                  podeEditar={podeGerenciarEquipe}
                  onAlternar={(id) => toggle({ id })}
                />
              </Secao>
            </>
          )}
        </div>

        {!detail.isLoading && podeGerenciarEquipe && (
          <div
            className="border-t border-zinc-100 px-5 py-4 dark:border-border"
            style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
          >
            <TabSaveBar
              dirty={dirty}
              // Abrir um membro que nunca customizou nada já vem com a sugestão marcada, e a
              // sugestão difere do que está salvo (nada) — dá pra salvar de cara, mas "alterações
              // não salvas" sem ninguém ter tocado em nada era alarme falso.
              avisar={selectedIds !== null && dirty}
              isPending={update.isPending}
              onSave={() =>
                update.mutate({ membershipId: membershipId!, procedureTypeIds: baseline })
              }
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
